// src/components/OtpInput.jsx
// Six-box OTP entry — auto-advance on type, backspace moves back,
// paste splits a full code across all boxes. Reused by signup verify,
// forgot-password confirm, and profile email-change confirm.

import React, { useRef, useEffect } from 'react';
import styles from './OtpInput.module.css';

export default function OtpInput({ value, onChange, disabled, autoFocus = true }) {
  const inputRefs = useRef([]);
  const digits = value.split('').concat(Array(6).fill('')).slice(0, 6);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const setDigit = (idx, digit) => {
    const next = [...digits];
    next[idx] = digit;
    onChange(next.join(''));
  };

  const handleChange = (idx, e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) { setDigit(idx, ''); return; }
    // Handle paste-into-single-box or fast typing
    if (raw.length > 1) {
      const chars = raw.split('').slice(0, 6 - idx);
      const next = [...digits];
      chars.forEach((c, i) => { next[idx + i] = c; });
      onChange(next.join(''));
      const lastFilled = Math.min(idx + chars.length, 5);
      inputRefs.current[lastFilled]?.focus();
      return;
    }
    setDigit(idx, raw);
    if (idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    onChange(pasted.padEnd(6, '').slice(0, 6).replace(/ /g, ''));
    const focusIdx = Math.min(pasted.length, 5);
    inputRefs.current[focusIdx]?.focus();
  };

  return (
    <div className={styles.otpRow} onPaste={handlePaste}>
      {digits.map((digit, idx) => (
        <input
          key={idx}
          ref={el => (inputRefs.current[idx] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          className={styles.otpBox}
          value={digit}
          disabled={disabled}
          onChange={e => handleChange(idx, e)}
          onKeyDown={e => handleKeyDown(idx, e)}
        />
      ))}
    </div>
  );
}